/* GTM TRACKER — how far each APPROVED programme has got (So-yeon Shin, 2026-10-16, point 8: "Could you
   check whether a separate menu can be added to track the progress of programmes after approval?";
   Max: "làm luôn mục 8 nha").

   THE TIMELINE IS THE PLAN. Every programme already carries its run as Timeline · Owners (stage, what
   happens, expected date, owner); after approval the same steps are ticked off here — Not started, In
   progress, Done (with the day it was done). Nothing is planned twice. The server works out the
   figures (done, in progress, overdue, the next step), so the cards, the bars and the flags agree.

   A step is OVERDUE when its expected date has passed and it is not done — red, and said in words
   beside the date, not by colour alone. Editors (gtm_programme at edit) change a step's status here;
   everybody else who may read the programme reads. */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import Header from './Header';
import ModuleTabs from './ModuleTabs';
import ModuleHelp from './ModuleHelp';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './ProjectApproval.css';
import './Gtm.css';

const API = `${BACKEND_URL}/api`;
const STATES = ['not_started', 'in_progress', 'done'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// 20/Nov/2026 — a month name, never 20/11, as everywhere else in the portal.
const dash = <span className="pa-muted">—</span>;
const fmt = (d) => {
  if (!d) return '';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  return `${dd}/${MONTHS[Number(m) - 1]}/${y}`;
};

/* THE GUIDE (Max 01/11: every module has one, ending on the manual). The table and its fold button are drawn only once
   a programme has been approved, so those steps are skipped on an empty tracker. */
const TOUR = [
  { selector: '.gtm-trk-kpis', title: 'tourGtmTracker.kpisT', body: 'tourGtmTracker.kpisB' },
  { selector: '.gtm-kpi.rew', title: 'tourGtmTracker.overdueT', body: 'tourGtmTracker.overdueB' },
  { selector: '.gtm-trk-tools', title: 'tourGtmTracker.toolsT', body: 'tourGtmTracker.toolsB' },
  { selector: '.gtm-trk-ltable', title: 'tourGtmTracker.tableT', body: 'tourGtmTracker.tableB' },
  { selector: '.gtm-trk-row .gtm-trk-fold', title: 'tourGtmTracker.foldT', body: 'tourGtmTracker.foldB' },
];

export default function GtmTracker() {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  /* ONE ROW PER PROGRAMME, its steps folded under it — Max, 2026-10-16: "trường hợp có 50 cái GTM thì
     thể hiện như vầy có hợp lý không". A card per programme with every step open is a page nobody
     reads past the third; a table of fifty rows sorts, filters and shows at a glance which one is
     late. Nothing opens by itself. */
  const [open, setOpen] = useState(() => new Set());

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/gtm-tracker`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setD(j); setErr('');
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (code, step, status) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/gtm/${encodeURIComponent(code)}/timeline/${step.id}/progress`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const rows = d?.rows || [];
  const totals = rows.reduce((a, r) => ({
    steps: a.steps + r.total, done: a.done + r.done, prog: a.prog + r.inProgress, over: a.over + r.overdue,
  }), { steps: 0, done: 0, prog: 0, over: 0 });
  const needle = q.trim().toLowerCase();
  /* What needs attention leads: programmes with an overdue step, then the nearest next date. A
     column header the reader presses re-sorts from there. */
  const pre = rows.filter((r) => (!onlyOverdue || r.overdue > 0)
    && (!needle || [r.code, r.title, r.owner, ...r.steps.map((x) => `${x.label} ${x.owner}`)].join(' ').toLowerCase().includes(needle)))
    .slice().sort((a, b) => (b.overdue > 0) - (a.overdue > 0)
      || String(a.next?.planDate || '9999').localeCompare(String(b.next?.planDate || '9999')));
  const toggle = (code) => setOpen((cur) => { const n = new Set(cur); if (n.has(code)) n.delete(code); else n.add(code); return n; });

  /* The columns, each sortable and filterable on what its cell shows (Excel's funnels, as on the
     other GTM lists). Dates sort and filter as ISO days; the cell prints them with a month name. */
  const columns = [
    { key: 'code', label: t('gtm.colCode'), get: (r) => r.code, td: 'mono' },
    { key: 'title', label: t('gtm.colTitle'), get: (r) => r.title, td: 'gtm-ltitle',
      cell: (r) => <Link to={`/gtm/${encodeURIComponent(r.code)}`} onClick={(e) => e.stopPropagation()}>{r.title}</Link> },
    { key: 'owner', label: t('gtm.colOwner'), get: (r) => r.owner },
    { key: 'gtmLaunch', label: t('gtm.colLaunch'), get: (r) => r.launch || '', cell: (r) => (r.launch ? fmt(r.launch) : dash) },
    { key: 'progress', label: t('gtm.trkProgress'), get: (r) => (r.total ? r.pct : null), th: 'gtm-trk-progcol',
      cell: (r) => (r.total ? (
        <div className="gtm-trk-cellbar">
          <div className="gtm-trk-bar" role="img" aria-label={t('gtm.trkStepsOf', { done: r.done, total: r.total })}>
            <span className="d" style={{ width: `${(r.done / r.total) * 100}%` }} />
            <span className="p" style={{ width: `${(r.inProgress / r.total) * 100}%` }} />
          </div>
          <span className="gtm-trk-frac">{r.done}/{r.total} · <b>{r.pct}%</b></span>
        </div>
      ) : <span className="pa-muted">{t('gtm.trkNoSteps')}</span>) },
    { key: 'inprog', label: t('gtm.st_in_progress'), get: (r) => r.inProgress, td: 'num',
      cell: (r) => (r.inProgress || <span className="pa-muted">0</span>) },
    { key: 'overdue', label: t('gtm.trkOverdue'), get: (r) => r.overdue, td: (r) => `num${r.overdue ? ' pa-warn gtm-trk-overcell' : ''}`,
      cell: (r) => (r.overdue ? <b>{r.overdue}</b> : <span className="pa-muted">0</span>) },
    { key: 'next', label: t('gtm.trkNext'), get: (r) => (r.next ? r.next.label : (r.total ? t('gtm.trkAllDone') : '')),
      cell: (r) => (r.total === 0 ? dash : r.next
        ? <><b>{r.next.label}</b>{r.next.owner ? <span className="pa-muted"> · {r.next.owner}</span> : null}</>
        : <span className="gtm-trk-alldone">{t('gtm.trkAllDone')}</span>) },
    { key: 'gtmNextDue', label: t('gtm.trkNextDue'), get: (r) => r.next?.planDate || '',
      cell: (r) => (r.next?.planDate ? <>{fmt(r.next.planDate)}{r.next.overdue && <span className="gtm-trk-late">{t('gtm.trkOverdue')}</span>}</> : dash) },
    { key: 'gtmApprovedOn', label: t('gtm.trkApprovedOn'), get: (r) => (r.decidedAt ? String(r.decidedAt).slice(0, 10) : ''),
      cell: (r) => (r.decidedAt ? <>{fmt(r.decidedAt)}<span className="pa-muted"> · {r.decidedBy || r.approver}</span></> : dash) },
  ];
  const tools = useColumnTools(pre, columns);

  if (!d) {
    return (
      <div className="sp-page"><Header /><div className="sp-main"><ModuleTabs module="gtm" />
        {err ? <div className="pa-banner err">{err}</div>
          : <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('gtm.loading')}</div>}
      </div></div>
    );
  }

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main gtm-listpage gtm-trk-page">
        <ModuleTabs module="gtm" />
        <div className="mh-titlerow">
          <div className="pa-title">
            <h2>{t('gtm.trkTitle')}</h2>
            <p>{t('gtm.trkSub')}</p>
          </div>
          <ModuleHelp manual="gtm" steps={TOUR} tourKey="gtmTracker" ready={!!d} />
        </div>
        {err && <div className="pa-banner err">{err}</div>}

        <div className="gtm-kpis gtm-trk-kpis">
          <div className="gtm-kpi conf as-card"><div className="kl">{t('gtm.trkApproved')}</div><div className="kv">{rows.length}</div></div>
          <div className="gtm-kpi as-card"><div className="kl">{t('gtm.trkStepsDone')}</div>
            <div className="kv">{totals.done}<span className="gtm-trk-of"> / {totals.steps}</span></div></div>
          <div className="gtm-kpi pend as-card"><div className="kl">{t('gtm.st_in_progress')}</div><div className="kv">{totals.prog}</div></div>
          {/* The one card that filters: it is the question somebody opens this screen to ask. */}
          <button type="button" className={`gtm-kpi rew${onlyOverdue ? ' active' : ''}`} aria-pressed={onlyOverdue}
            onClick={() => setOnlyOverdue((v) => !v)} title={t('gtm.trkOnlyOverdue')}>
            <div className="kl">{t('gtm.trkOverdue')}</div><div className="kv">{totals.over}</div>
          </button>
        </div>

        <div className="pa-toolbar gtm-trk-tools">
          <input className="gtm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('gtm.trkSearch')} />
          {(q || onlyOverdue || tools.active) && (
            <button type="button" className="pa-btn" onClick={() => { setQ(''); setOnlyOverdue(false); tools.clear(); }}>{t('gtm.clear')}</button>
          )}
          <span className="pa-muted">{t('gtm.countOf', { n: tools.shown.length, total: rows.length })} · {t('gtm.trkToday', { day: fmt(d.today) })}</span>
        </div>

        {!rows.length ? (
          <div className="pa-section"><p className="pa-muted" style={{ margin: 0 }}>{t('gtm.trkNone')}</p></div>
        ) : (
          <div className="pa-section gtm-trk-list">
            <div className="pa-table-wrap">
              <table className="pa-table gtm-ltable gtm-trk-ltable">
                <thead>
                  <tr>
                    <th className="gtm-trk-foldcol" aria-label={t('xGtm.colFold')} />
                    {columns.map((c) => <SortTh key={c.key} label={c.label} className={c.th || ''} {...tools.thProps(c.key)} />)}
                  </tr>
                </thead>
                <tbody>
                  {!tools.shown.length && (
                    <tr><td colSpan={columns.length + 1} className="pa-muted">{t('gtm.noMatch')}</td></tr>
                  )}
                  {tools.shown.map((r) => {
                    const isOpen = open.has(r.code);
                    return (
                      <React.Fragment key={r.code}>
                        {/* The whole row opens it; the title link opens the programme instead. */}
                        <tr data-trk={r.code} className={`gtm-trk-row${r.overdue ? ' late' : ''}${isOpen ? ' open' : ''}`}
                          onClick={() => toggle(r.code)}>
                          <td className="gtm-trk-foldcol">
                            <button type="button" className="gtm-trk-fold" aria-expanded={isOpen}
                              aria-label={isOpen ? t('gtm.collapseAll') : t('gtm.expandAll')}
                              onClick={(e) => { e.stopPropagation(); toggle(r.code); }}>
                              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          </td>
                          {columns.map((c) => (
                            <td key={c.key} className={typeof c.td === 'function' ? c.td(r) : (c.td || '')}>
                              {c.cell ? c.cell(r) : (c.get(r) === '' || c.get(r) == null ? dash : c.get(r))}
                            </td>
                          ))}
                        </tr>
                        {isOpen && (
                          <tr className="gtm-trk-detail">
                            <td colSpan={columns.length + 1}>{steps(r)}</td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  /* A programme's steps, under its row. A function rather than a component: declared in the render,
     a component would be a new type every time and React would rebuild the table on each change. */
  function steps(r) {
    if (!r.steps.length) return <p className="pa-muted gtm-trk-empty">{t('gtm.trkNoStepsHint')}</p>;
    return (
      <table className="pa-table gtm-trk-table">
        <colgroup>
          <col style={{ width: 44 }} /><col style={{ width: 200 }} /><col />
          <col style={{ width: 170 }} /><col style={{ width: 150 }} /><col style={{ width: 160 }} /><col style={{ width: 120 }} />
        </colgroup>
        <thead><tr>
          <th className="num">#</th><th>{t('gtm.timelineStage')}</th><th>{t('gtm.timelineWho')}</th>
          <th>{t('gtm.timelineOwner')}</th><th>{t('gtm.timelineWhen')}</th><th>{t('gtm.trkStatus')}</th><th>{t('gtm.trkDoneOn')}</th>
        </tr></thead>
        <tbody>
          {r.steps.map((x) => (
            <tr key={x.id} data-step={x.id} className={x.overdue ? 'late' : x.status === 'done' ? 'done' : undefined}>
              <td className="num mono">{x.no}</td>
              <td><b>{x.label}</b></td>
              <td className="gtm-trk-det">{x.detail || dash}</td>
              <td>{x.owner || dash}</td>
              <td>
                {x.planDate ? fmt(x.planDate) : dash}
                {x.overdue && <span className="gtm-trk-late">{t('gtm.trkOverdue')}</span>}
              </td>
              <td>
                {d.canEdit ? (
                  <select className={`gtm-trk-st ${x.status}`} value={x.status} disabled={busy}
                    aria-label={`${t('gtm.trkStatus')}: ${x.label}`}
                    onChange={(e) => setStatus(r.code, x, e.target.value)}>
                    {STATES.map((k) => <option key={k} value={k}>{t(`gtm.st_${k}`)}</option>)}
                  </select>
                ) : <span className={`gtm-trk-chip ${x.status}`}>{t(`gtm.st_${x.status}`)}</span>}
              </td>
              <td>{x.doneDate ? fmt(x.doneDate) : dash}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
}
